import React, { useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import useWeeklyUsage from "../hooks/useWeeklyUsage";

/**
 * Píldora de uso semanal (item Planner #1209, mockup E · posición 3).
 *
 * AVISA, NO BLOQUEA — mismo criterio que el ContextMeter del Turno. Nada de la
 * app se apaga por lo que diga esta barra: el único corte real sigue siendo el
 * tope diario en USD del backend, que tiene su propio diálogo.
 *
 * Muestra el PORCENTAJE del plan del médico, nunca dólares ni tokens: el costo
 * de la API es dato interno y el backend directamente no lo manda.
 *
 * Dos presentaciones del mismo dato:
 *   variant="pill"      → desktop ancho, suelta a la izquierda del cluster del
 *                         navbar, abre el desglose en un popover.
 *   variant="mini"      → misma píldora sin el texto ni la barra (solo el punto
 *                         de color y el %), para laptops donde la completa se
 *                         montaría sobre el botón ≡ del navbar.
 *   variant="expanded"  → dentro del menú, en TODO ancho: es la única
 *                         superficie bajo 1200px (donde no hay píldora), y
 *                         arriba de eso evita que el dato desaparezca justo
 *                         cuando el menú abierto tapa la píldora.
 *
 * Si el endpoint no responde, `useWeeklyUsage` devuelve null y acá no se
 * dibuja nada. Nunca un error en pantalla.
 */

// El nivel lo decide el backend (umbrales 60/85 en usage_report.py). Acá solo
// se traduce a token de color — ningún literal, regla de theming del proyecto.
const COLOR_POR_NIVEL = {
  ok: "var(--color-primary-soft)",
  warn: "var(--color-warning)",
  over: "var(--color-danger)",
};

const DIAS = [
  "domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado",
];

// "2026-09-08" → "lunes". Se parsea a mano y no con new Date(iso), que
// interpreta la fecha en UTC y en Chile devuelve el día anterior.
const diaDeLaSemana = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return "";
  return DIAS[new Date(y, m - 1, d).getDay()] || "";
};

const minutos = (segundos) => Math.round((Number(segundos) || 0) / 60);

const Barra = ({ percent, color, alto = 7 }) => (
  <Box
    sx={{
      height: alto,
      borderRadius: 999,
      backgroundColor: "rgba(var(--fg-rgb),0.09)",
      overflow: "hidden",
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        // Sobre 100% la barra queda llena: el porcentaje sigue subiendo en el
        // texto, que es el dato que importa para decidir un cambio de plan.
        width: `${Math.min(100, Math.max(0, percent))}%`,
        height: "100%",
        borderRadius: 999,
        backgroundColor: color,
        transition: "width .5s cubic-bezier(0.22,1,0.36,1), background-color .3s ease",
      }}
    />
  </Box>
);

// "35 de 250" cuando el plan tiene tope; "35" solo cuando no lo tiene. El
// límite del plan se muestra para que el médico sepa cuánto le queda, no para
// avisarle que lo van a cortar: no lo cortan nunca.
//
// `null` cuando el dato NO VINO, que no es lo mismo que un límite ausente. Pasa
// de verdad: el frontend se deploya a mano con wrangler y el backend por
// pipeline, así que el front puede aterrizar primero y hablarle a un backend que
// todavía no manda `documentos_mes`. Sin esta distinción el médico leía
// literalmente "Este mes undefined", sin error de consola que lo delatara.
const conLimite = (usado, limite) => {
  if (usado === null || usado === undefined) return null;
  return limite ? `${usado} de ${limite}` : `${usado}`;
};

const Desglose = ({ usage }) => (
  <Box sx={{ mt: 1.5, fontSize: 12.5, color: "var(--text-secondary)" }}>
    {[
      ["Esta semana", conLimite(usage.documentos, usage.limite_semanal), "weekly-usage-semana"],
      ["Este mes", conLimite(usage.documentos_mes, usage.limite_mensual), "weekly-usage-mes"],
      ["Dictado", `${minutos(usage.dictado_segundos)} min`, null],
      ["Días trabajados", usage.dias_con_actividad, null],
    ]
      // Una fila sin dato no se dibuja. Mejor que falte a que mienta.
      .filter(([, valor]) => valor !== null && valor !== undefined)
      .map(([etiqueta, valor, testid]) => (
      <Box key={etiqueta} sx={{ display: "flex", justifyContent: "space-between", py: 0.35 }}>
        <span>{etiqueta}</span>
        <b
          {...(testid ? { "data-testid": testid } : {})}
          style={{ color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}
        >
          {valor}
        </b>
      </Box>
      ))}
  </Box>
);

const WeeklyUsagePill = ({ variant = "pill" }) => {
  const mini = variant === "mini";
  const usage = useWeeklyUsage();
  const [anchor, setAnchor] = useState(null);

  if (!usage) return null;

  // Sin tope (interno, cuenta técnica, Max+ — `gratis` SÍ tiene): hay consumo
  // pero no hay proporción. Se muestran los documentos y NADA de barra ni de
  // color de alarma — inventar un porcentaje sería mentirle al médico sobre un
  // plan que no tiene.
  const sinTope = usage.percent === null || usage.percent === undefined;
  const color = sinTope
    ? "var(--color-primary-soft)"
    : COLOR_POR_NIVEL[usage.nivel] || COLOR_POR_NIVEL.ok;
  const cifra = sinTope ? `${usage.documentos}` : `${usage.percent}%`;
  const renueva = diaDeLaSemana(usage.renueva);
  const pie = renueva ? `Se renueva el ${renueva}.` : "";

  if (variant === "expanded") {
    return (
      <Box data-testid="weekly-usage-pill" sx={{ flexBasis: "100%", width: "100%", py: 0.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.75 }}>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
            Uso de la semana
          </Typography>
          <Typography
            data-testid="weekly-usage-percent"
            sx={{ fontSize: 12.5, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}
          >
            {cifra}
          </Typography>
        </Box>
        {!sinTope && <Barra percent={usage.percent} color={color} />}
        <Typography sx={{ fontSize: 11, color: "var(--gray-faint)", mt: 0.75 }}>
          {/* El mes va también acá: en celular esta variante es la ÚNICA que
              existe, y si el mes es lo que se vende, el que trabaja desde el
              teléfono tiene que poder verlo (lo marcó el revisor: la barra
              expandida no dibuja el desglose). */}
          {conLimite(usage.documentos, usage.limite_semanal)} esta semana
          {usage.documentos_mes !== null && usage.documentos_mes !== undefined && (
            <>
              {" · "}
              <span data-testid="weekly-usage-mes-expandida">
                {conLimite(usage.documentos_mes, usage.limite_mensual)} este mes
              </span>
            </>
          )}
          {pie ? ` · ${pie}` : ""}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        data-testid="weekly-usage-pill"
        role="button"
        tabIndex={0}
        aria-label={
          sinTope
            ? `Uso de la semana: ${usage.documentos} documentos`
            : `Uso de la semana: ${usage.percent} por ciento`
        }
        // En la variante compacta el texto no entra, así que el tooltip nativo
        // es lo único que le dice al que MIRA de qué es ese número (el
        // aria-label solo resuelve al lector de pantalla).
        title={mini ? `Uso de la semana: ${cifra}` : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setAnchor(e.currentTarget);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.9,
          height: 38,
          px: mini ? 1.15 : 1.4,
          borderRadius: 999,
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontSize: 12.5,
          color: "var(--text-primary)",
          backgroundColor: "rgba(var(--fg-rgb),0.05)",
          border: "1px solid rgba(var(--fg-rgb),0.1)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 8px 20px rgba(var(--shadow-rgb),0.24)",
          transition: "background-color .2s ease",
          "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.1)" },
          "&:focus-visible": { outline: `2px solid ${color}`, outlineOffset: 2 },
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
        {!mini && "Uso semanal"}
        {!mini && !sinTope && (
          <Box sx={{ width: 44 }}>
            <Barra percent={usage.percent} color={color} alto={6} />
          </Box>
        )}
        <b
          data-testid="weekly-usage-percent"
          style={{ color, fontVariantNumeric: "tabular-nums" }}
        >
          {cifra}
        </b>
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // MUI 5.13 ignora slotProps.paper (llegó en 5.15): va PaperProps o el
        // Paper sale con el gris del tema en vez del glass.
        PaperProps={{
          "data-testid": "weekly-usage-popover",
          sx: {
            mt: 1,
            width: 280,
            p: 2,
            borderRadius: "14px",
            backgroundColor: "var(--bg-elevated)",
            backgroundImage: "none",
            backdropFilter: "blur(18px) saturate(140%)",
            WebkitBackdropFilter: "blur(18px) saturate(140%)",
            border: "1px solid rgba(var(--fg-rgb),0.12)",
            boxShadow: "0 14px 40px rgba(var(--shadow-rgb),0.45)",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.75 }}>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
            Uso de la semana
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
            {cifra}
          </Typography>
        </Box>
        {!sinTope && <Barra percent={usage.percent} color={color} />}
        <Desglose usage={usage} />
        <Typography sx={{ fontSize: 11, color: "var(--gray-faint)", mt: 1.25, lineHeight: 1.5 }}>
          {/* La promesa de que no se corta solo tiene sentido si hay un tope del
              cual pasarse. A un plan sin tope decirle "si llegas al 100%" es
              hablarle de un número que no existe. */}
          {pie}
          {!sinTope && " Si llegas al 100% no se corta nada: seguimos trabajando igual."}
        </Typography>
      </Popover>
    </>
  );
};

export default WeeklyUsagePill;
